import { FC, useEffect, useState } from 'react';
import { Descriptions, Radio, Select } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/rootReducer';
import { getDetailCode, getReverseYears } from '../../../lib/util';
import { YN } from '../../../models/common.api.model';
import Button from '../../Button';
import { useHistory } from 'react-router-dom';
import useNotification from '../../../hooks/useNotification';
import {
  ISpeakingBody,
  ISpeakingDetail,
} from '../../../models/newSpeaking.model';
import SpeakingPartTable from '../../../pages/cbt/speaking-test/part/SpeakingPartTable';
import Tinymce from '../../Tinymce';
import useAuth from '../../../hooks/useAuth';
import SelectEduStep from '../../SelectEduStep';

interface IProps {
  data?: ISpeakingDetail;
  submit(body: ISpeakingBody): void;
};

const SpeakingTestEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { infoNotification } = useNotification();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const TE0008 = getDetailCode(commonCodes, 'TE0008');
  const TE0003 = getDetailCode(commonCodes, 'TE0003');
  const TE0010 = getDetailCode(commonCodes, 'TE0010');
  const TE0012 = getDetailCode(commonCodes, 'TE0012');
  const SY0005 = getDetailCode(commonCodes, 'SY0005');
  const SY0011 = getDetailCode(commonCodes, 'SY0011');

  const [eduStepCode, setEduStepCode] = useState<string>();
  const [rubricType, setRubricType] = useState<string>();
  const [examName, setExamName] = useState('');
  const [examType, setExamType] = useState<string>();
  const [speakingExamType, setSpeakingExamType] = useState<string>();
  const [year, setYear] = useState<string>();
  const [testSeq, setTestSeq] = useState<string>();
  const [useYN, setUseYN] = useState<YN>('N');
  const [direction, setDirection] = useState('');

  useEffect(() => {
    if (data) {
      setEduStepCode(data.eduStepCode);
      setRubricType(data.rubricType);
      setExamName(data.examName);
      setExamType(data.examType);
      setSpeakingExamType(data.speakingExamType);
      setYear(data.year);
      setTestSeq(data.testSeq);
      setUseYN(data.useYN);
      setDirection(data.direction);
    }
  }, [data]);

  const onSubmit = () => {
    if (!eduStepCode) {
      return infoNotification(t('cbt.speakingRubric.1'));
    }
    if (!rubricType) {
      return infoNotification(t('cbt.speakingRubric.8'));
    }
    if (!examName) {
      return infoNotification(t('cbt.speakRubric.message1'));
    }
    if (!examType) {
      return infoNotification(t('cbt.speakingRubric.9'));
    }
    if (!speakingExamType) {
      return infoNotification(t('cbt.speakTest.message2'));
    }
    if (!year) {
      return infoNotification(t('cbt.speakTest.message3'));
    }
    if (!testSeq) {
      return infoNotification(t('cbt.speakTest.message4'));
    }
    if (!direction) {
      return infoNotification(t('cbt.speakTest.message5'));
    }

    submit({
      eduStepCode,
      rubricType,
      examName,
      examType,
      speakingExamType,
      year,
      testSeq,
      direction,
      useYN,
    });
  };

  const showPreview = () => {
    if (
      (eduStepCode === 'EM' && (data?.papers?.length || 0) >= 2) ||
      (eduStepCode === 'MD' && (data?.papers?.length || 0) >= 1) 
    ) {
      window.open(
        `/cbt/speaking-test/preview/${data?.speakingExamCode}`,
        '_blank',
      );
    } else {
      infoNotification(t('cbt.speakTest.message6'));
    }
  };

  return (
    <>
      <div className="comm-bx__between">
        <h4 className='sub-title'>{t('cbt.speakTest.title3')}</h4>
      </div>
      <div className='comm-table__area rowType'>
        <Descriptions
          bordered
          column={2}
          labelStyle={{ textAlign: 'center' }}
        >
          <Descriptions.Item label={t('common.label.1')} span={2}>
            <SelectEduStep
              eduStepCode={eduStepCode}
              setEduStepCode={setEduStepCode}
              placeholder={t('student.student.placeholder6')}
              width={150}
              disabled={!!data}
              noDefault
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakingRubric.10')} span={2}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('student.student.placeholder6')}
                value={rubricType}
                onChange={value => {
                  setRubricType(value);
                  setSpeakingExamType(undefined);
                }}
                disabled={!!data}
              >
                {
                  TE0010?.map(item => (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  ))
                }
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column1')} span={2}>
            <input
              type="text"
              className="form-control"
              value={examName}
              onChange={e => setExamName(e.target.value)}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column7')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={examType}
                onChange={setExamType}
              >
                {TE0008?.map((item) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column3')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={speakingExamType}
                onChange={setSpeakingExamType}
                disabled={!rubricType}
              >
                {(rubricType === 'ST' ? TE0003 : TE0012)?.map((item) => (
                  <Select.Option key={item.detailCode} value={item.detailCode}>
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column4')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={year}
                onChange={setYear}
              >
                {getReverseYears(SY0005).map((item) => (
                  <Select.Option key={item.detailCode} value={item.detailCode}>
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column5')}>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={testSeq}
                onChange={setTestSeq}
              >
                {SY0011?.map((item) => (
                  <Select.Option key={item.detailCode} value={item.detailCode}>
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column6')} span={2}>
            <div className='auth-radio__area'>
              <Radio.Group
                onChange={({ target: { value } }) => setUseYN(value)}
                value={useYN}
              >
                <Radio value="Y">Y</Radio>
                <Radio value="N">N</Radio>
              </Radio.Group>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Instruction" span={2}>
            <Tinymce content={direction} setContent={setDirection} />
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push('/cbt/speaking-test')}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={showPreview}
            text={t('cbt.speakTest.button2')}
          />
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      {data && (
        <>
          <div className="comm-bx__between">
            <h3 className='sub-title'>Part</h3>
          </div>
          <div className='comm-table__area'>
            <SpeakingPartTable
              speakingExamCode={data.speakingExamCode}
              data={data.papers}
            />
          </div>
        </>
      )}
    </>
  );
};

export default SpeakingTestEditor;
